import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { ArrowLeft, Send, MessageSquare, Smile } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import CrisisBanner from "@/components/CrisisBanner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const REACTION_EMOJIS = ["👍", "❤️", "😂", "🙏", "💪", "✅"];

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [typingFromOther, setTypingFromOther] = useState(null); // {name, at}
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const bottomRef = useRef(null);

  const isProvider = user?.role === "admin";

  const loadConversations = async () => {
    try {
      const { data } = await API.get("/conversations");
      setConversations(data || []);
      return data || [];
    } catch { return []; }
  };

  // Patient view: always show ONE thread. Merge any duplicate convos into the oldest one,
  // then auto-select it. Provider view keeps the inbox behavior.
  useEffect(() => {
    if (!user) return;
    (async () => {
      const data = await loadConversations();
      if (!isProvider) {
        if (data.length === 0) {
          // Seed the patient's single lifelong thread
          try {
            const { data: newConvo } = await API.post("/conversations", {
              provider_name: "Care Team",
              subject: "Messages",
              status: "active",
              unread_count: 0,
            });
            setConversations([newConvo]);
            setSelectedConvo(newConvo);
          } catch { }
        } else {
          // Use the oldest convo as the canonical one. (If there are >1 because of legacy
          // duplicates, we still only display one — the others are simply hidden from the
          // patient and will be consolidated server-side as admin tooling cleans them up.)
          const sorted = [...data].sort((a, b) =>
            new Date(a.created_date || 0) - new Date(b.created_date || 0)
          );
          const canonical = sorted[0];
          setConversations([canonical]);
          setSelectedConvo(canonical);
        }
      }
      setLoading(false);
    })();

    // Mark all "new_message" notifications as read when Messages page is opened
    API.post("/notifications/mark-read-by-type", { type: "new_message" })
      .then(() => window.dispatchEvent(new CustomEvent("notifications:refresh")))
      .catch(() => {});
  }, [user, isProvider]);

  // When a convo is selected → load its messages + mark it read
  useEffect(() => {
    if (!selectedConvo) return;
    API.get(`/messages/${selectedConvo.id}`).then(({ data }) => setMessages(data || [])).catch(() => {});
    if (selectedConvo.unread_count > 0) {
      API.put(`/conversations/${selectedConvo.id}`, { unread_count: 0 }).catch(() => {});
      setConversations(prev => prev.map(c => c.id === selectedConvo.id ? { ...c, unread_count: 0 } : c));
    }
    const interval = setInterval(() => {
      API.get(`/messages/${selectedConvo.id}`).then(({ data }) => setMessages(data || [])).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedConvo]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Listen to WS events for reactions + typing (broadcast by the backend's
  // /messages/{id}/reaction and /messages/typing endpoints).
  useEffect(() => {
    if (!selectedConvo?.id) return;
    const handler = (e) => {
      const evt = e?.detail;
      if (!evt || evt.conversation_id !== selectedConvo.id) return;
      if (evt.type === "reaction") {
        setMessages(prev => prev.map(m => m.id === evt.message_id ? { ...m, reactions: evt.reactions } : m));
      } else if (evt.type === "typing" && evt.user_id !== user?.id) {
        setTypingFromOther({ name: evt.name || "Someone", at: Date.now() });
      }
    };
    window.addEventListener("realtime:message_event", handler);
    return () => window.removeEventListener("realtime:message_event", handler);
  }, [selectedConvo?.id, user?.id]);

  // Clear stale typing indicator after 3s
  useEffect(() => {
    if (!typingFromOther) return;
    const t = setTimeout(() => setTypingFromOther(null), 3000);
    return () => clearTimeout(t);
  }, [typingFromOther]);

  const pingTyping = () => {
    if (!selectedConvo?.id) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2500) return; // throttle to once every 2.5s
    lastTypingSentRef.current = now;
    API.post("/messages/typing", { conversation_id: selectedConvo.id }).catch(() => {});
  };

  const toggleReaction = async (messageId, emoji) => {
    try {
      const { data } = await API.post(`/messages/${messageId}/reaction`, { emoji });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions } : m));
    } catch { }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending || !selectedConvo) return;
    setSending(true);
    const content = newMessage.trim();
    try {
      await API.post("/messages", {
        conversation_id: selectedConvo.id,
        sender_email: user.email,
        sender_name: user.full_name || "Patient",
        sender_role: user.role === "admin" ? "provider" : "patient",
        content,
        is_read: false,
      });
      await API.put(`/conversations/${selectedConvo.id}`, {
        last_message_preview: content.substring(0, 100),
        last_message_date: new Date().toISOString(),
      });
      setNewMessage("");
      const { data } = await API.get(`/messages/${selectedConvo.id}`);
      setMessages(data || []);
      // refresh inbox preview
      loadConversations();
    } catch { }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-140px)]" data-testid="messages-loading">
        <MessageSquare className="w-8 h-8 text-muted-foreground animate-pulse" />
      </div>
    );
  }

  // ───── Thread view ─────
  if (selectedConvo) {
    return (
      <div className="flex flex-col h-[calc(100vh-64px-80px)] md:h-[calc(100vh-64px)] max-w-lg mx-auto" data-testid="messages-thread-view">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3 bg-card">
          {isProvider ? (
            <button
              onClick={() => { setSelectedConvo(null); setMessages([]); loadConversations(); }}
              className="p-1 rounded-lg hover:bg-accent transition-colors"
              data-testid="messages-back-to-inbox"
              aria-label="Back to inbox"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => navigate("/")}
              className="p-1 rounded-lg hover:bg-accent transition-colors"
              data-testid="messages-back-home"
              aria-label="Back home"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{isProvider ? (selectedConvo.patient_email || selectedConvo.patient_name || "Patient") : "Care Team"}</h3>
            <p className="text-xs text-muted-foreground truncate">{isProvider ? (selectedConvo.subject || "Conversation") : "Elevated Psychiatry"}</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {!isProvider && <CrisisBanner className="mb-2" />}
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Start the conversation below</p>
            </div>
          ) : messages.map((msg) => {
            const isOwn = msg.sender_email === user?.email;
            const reactions = Array.isArray(msg.reactions) ? msg.reactions : [];
            // Group by emoji for display
            const emojiCounts = reactions.reduce((acc, r) => { acc[r.emoji] = (acc[r.emoji] || 0) + 1; return acc; }, {});
            const mine = reactions.some(r => r.user_id === user?.id);
            return (
              <div key={msg.id} className={`flex ${isOwn ? "justify-end" : "justify-start"} group`}>
                <div className="relative max-w-[80%]">
                  <div className={`rounded-2xl px-4 py-2.5 ${isOwn ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md"}`}>
                    {!isOwn && <p className="text-[10px] font-medium text-muted-foreground mb-0.5">{msg.sender_name}</p>}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${isOwn ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                      {msg.created_date ? new Date(msg.created_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </p>
                  </div>
                  {/* Reaction trigger button */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className={`absolute -bottom-2 ${isOwn ? "-left-2" : "-right-2"} w-6 h-6 rounded-full bg-card border border-border shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${mine ? "!opacity-100" : ""}`}
                        aria-label="React"
                        data-testid="msg-react-button"
                      >
                        <Smile className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-1 flex gap-0.5" side="top" align={isOwn ? "start" : "end"}>
                      {REACTION_EMOJIS.map((e) => (
                        <button key={e} onClick={() => toggleReaction(msg.id, e)} className="w-8 h-8 rounded hover:bg-accent text-lg leading-none" data-testid={`msg-react-${e}`} aria-label={`React with ${e}`}>{e}</button>
                      ))}
                    </PopoverContent>
                  </Popover>
                  {/* Displayed reactions */}
                  {Object.keys(emojiCounts).length > 0 && (
                    <div className={`flex gap-1 mt-1 ${isOwn ? "justify-end" : "justify-start"}`} data-testid="msg-reactions">
                      {Object.entries(emojiCounts).map(([e, n]) => {
                        const iReacted = reactions.some(r => r.user_id === user?.id && r.emoji === e);
                        return (
                          <button
                            key={e}
                            onClick={() => toggleReaction(msg.id, e)}
                            className={`text-xs rounded-full px-2 py-0.5 border transition-colors ${iReacted ? "bg-primary/10 border-primary/30 text-foreground" : "bg-card border-border hover:bg-accent"}`}
                            data-testid={`msg-reaction-pill-${e}`}
                          >
                            {e} {n > 1 ? n : ""}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {typingFromOther && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-2.5 flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{typingFromOther.name} is typing</span>
                <span className="flex gap-0.5">
                  <span className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" />
                </span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div className="px-4 py-3 border-t border-border bg-card">
          <div className="flex items-end gap-2">
            <textarea
              value={newMessage}
              onChange={(e) => { setNewMessage(e.target.value); pingTyping(); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Type a message..."
              rows={1}
              data-testid="message-input"
              className="flex-1 resize-none bg-accent rounded-xl px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button size="icon" onClick={handleSend} disabled={!newMessage.trim() || sending} className="rounded-xl h-10 w-10 shrink-0" data-testid="send-message-button">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">Messages are forwarded to your care team via Spruce</p>
        </div>
      </div>
    );
  }

  // ───── Inbox view (provider only — patients auto-select their single thread above) ─────
  return (
    <div className="max-w-lg mx-auto px-4 py-4 animate-fade-in" data-testid="messages-inbox-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-accent transition-colors" aria-label="Back" data-testid="messages-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="font-display text-xl font-semibold">Messages</h2>
        <div className="w-9" />
      </div>

      {conversations.length === 0 ? (
        <div className="text-center py-16" data-testid="messages-empty-state">
          <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No conversations yet.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border bg-card rounded-2xl border border-border overflow-hidden" data-testid="messages-conversation-list">
          {conversations.map((c) => {
            const unread = (c.unread_count || 0) > 0;
            const timeLabel = c.last_message_date
              ? formatDistanceToNow(new Date(c.last_message_date), { addSuffix: true })
              : c.updated_date
              ? formatDistanceToNow(new Date(c.updated_date), { addSuffix: true })
              : "";
            const initials = (c.provider_name || c.patient_email || "?")
              .split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
            const title = user?.role === "admin"
              ? (c.patient_email || c.provider_name || "Conversation")
              : (c.provider_name || "Care Team");
            return (
              <li key={c.id}>
                <button
                  onClick={() => setSelectedConvo(c)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                  data-testid={`conversation-item-${c.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${unread ? "bg-primary text-primary-foreground" : "bg-accent text-foreground"}`}>
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm truncate ${unread ? "font-semibold" : "font-medium"}`}>{title}</p>
                      {timeLabel && <span className="text-[11px] text-muted-foreground shrink-0">{timeLabel}</span>}
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${unread ? "text-foreground" : "text-muted-foreground"}`}>
                      {c.last_message_preview || c.subject || "No messages yet"}
                    </p>
                  </div>
                  {unread && (
                    <span className="bg-secondary text-secondary-foreground text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center shrink-0" data-testid={`unread-badge-${c.id}`}>
                      {c.unread_count}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
